import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BookOpen, Download, ShoppingBag } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL;

export default function MyDownloadsPage() {
  const [loading, setLoading] = useState(true);
  const [downloads, setDownloads] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${API}/products/my-downloads`);
        setDownloads(r.data.downloads || []);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load your downloads');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const downloadFile = async (orderId, productId, name, fileType) => {
    try {
      const token = localStorage.getItem('token');
      const url = `${BACKEND_ORIGIN}/api/products/digital-download/${orderId}/${productId}`;
      const r = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || 'Download failed');
      }
      const blob = await r.blob();
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `${name}.${fileType || 'pdf'}`;
      link.click();
      window.URL.revokeObjectURL(link.href);
      toast.success('Download started');
    } catch (e) {
      toast.error(e.message || 'Download failed');
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1 max-w-5xl mx-auto w-full px-4 py-8" data-testid="my-downloads-page">
        <div className="mb-6">
          <h1 className="text-3xl font-heading font-bold flex items-center gap-3">
            <BookOpen className="h-7 w-7 text-primary" />
            My Downloads
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            All the digital books, PDFs, and study materials you have purchased. Unlimited re-downloads.
          </p>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 gap-4">
            {[0, 1, 2].map((i) => <div key={i} className="h-40 bg-muted rounded-lg animate-pulse" />)}
          </div>
        ) : downloads.length === 0 ? (
          <Card className="text-center py-16" data-testid="my-downloads-empty">
            <CardContent>
              <BookOpen className="h-16 w-16 mx-auto text-muted-foreground/40 mb-4" />
              <h2 className="text-xl font-heading font-medium mb-2">No digital purchases yet</h2>
              <p className="text-sm text-muted-foreground mb-6">
                Christian ebooks, PDF study guides, and audio sermons will appear here once you buy them.
              </p>
              <Button asChild size="lg">
                <a href="/products?category=Books" data-testid="browse-books-cta">
                  <ShoppingBag className="h-4 w-4 mr-2" />
                  Browse the Books catalog
                </a>
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {downloads.map((d) => (
              <Card key={`${d.order_id}-${d.product_id}`} className="overflow-hidden" data-testid={`download-card-${d.product_id}`}>
                <div className="flex gap-3 p-4">
                  {d.cover_image ? (
                    <img
                      src={d.cover_image.startsWith('http') ? d.cover_image : `${BACKEND_ORIGIN}${d.cover_image}`}
                      alt={d.name}
                      className="w-24 h-32 object-cover rounded-md flex-shrink-0"
                    />
                  ) : (
                    <div className="w-24 h-32 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
                      <BookOpen className="h-8 w-8 text-muted-foreground/40" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                      {(d.file_type || 'pdf').toUpperCase()}
                    </div>
                    <div className="font-heading font-medium text-base truncate mb-2" data-testid={`download-name-${d.product_id}`}>
                      {d.name}
                    </div>
                    {d.purchased_at && (
                      <div className="text-xs text-muted-foreground mb-3">
                        Purchased {new Date(d.purchased_at).toLocaleDateString()}
                      </div>
                    )}
                    <Button
                      size="sm"
                      onClick={() => downloadFile(d.order_id, d.product_id, d.name, d.file_type)}
                      className="w-full"
                      data-testid={`download-btn-${d.product_id}`}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
